import { Box } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import Autocomplete from 'components/autocomplete';
import Checkbox from 'components/checkbox';
import { useGrowthState } from '../context/growth-context';
import { GrowthFarm } from '../types/growth-farm.type';

type FarmSelectOption = { type: 'selectAll'; label: string } | { type: 'farm'; farm: GrowthFarm };

const GrowthFarmAutocomplete: React.FC = () => {
  const intl = useIntl();
  const { farmOptions, selectedFarmIds, setSelectedFarmIds } = useGrowthState();
  const total = farmOptions.length;
  const count = selectedFarmIds.length;
  const allSelected = total > 0 && count === total;
  const label = intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.filters.addFarm' });
  const farmSelectOptions: FarmSelectOption[] = [
    ...(total > 0
      ? [{ type: 'selectAll' as const, label: intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.filters.selectAll' }) }]
      : []),
    ...farmOptions.map((farm) => ({ type: 'farm' as const, farm })),
  ];
  const selectedOptions: FarmSelectOption[] = farmOptions
    .filter((farm) => selectedFarmIds.includes(farm.globalSiteId))
    .map((farm) => ({ type: 'farm', farm }));
  const renderSelectedFarms = (): string => {
    if (allSelected) return intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.filters.farmsAll' }, { total });
    return intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.filters.farmsSelected' }, { count, total });
  };

  const toggleAll = () => setSelectedFarmIds(allSelected ? [] : farmOptions.map((option) => option.globalSiteId));

  return (
    <Box minWidth={280} alignSelf="flex-start">
      <Autocomplete
        multiple
        label={label}
        options={farmSelectOptions}
        value={selectedOptions}
        clearText={intl.formatMessage({ id: 'benchmarkAnalytics.growthPage.filters.clear' })}
        noOptionsText={intl.formatMessage({ id: 'general.noResults' })}
        getOptionLabel={(option) => (option.type === 'selectAll' ? option.label : option.farm.name)}
        isOptionEqualToValue={(option, value) => {
          if (option.type === 'selectAll') return value.type === 'selectAll';
          return value.type === 'farm' && option.farm.globalSiteId === value.farm.globalSiteId;
        }}
        renderValue={(value) => (value.length > 0 ? renderSelectedFarms() : null)}
        renderOption={({ key, ...optionProps }, option) => (
          <Box
            key={key}
            component="li"
            {...optionProps}
            display="flex"
            alignItems="center"
            gap={1}>
            <Checkbox
              hiddenLabel
              checked={
                option.type === 'selectAll' ? allSelected : selectedFarmIds.includes(option.farm.globalSiteId)
              }
            />
            {option.type === 'selectAll' ? option.label : option.farm.name}
          </Box>
        )}
        onChange={(_, value) => {
          if (value.some((option) => option.type === 'selectAll')) {
            toggleAll();
            return;
          }
          setSelectedFarmIds(
            value.filter((option) => option.type === 'farm').map((option) => option.farm.globalSiteId)
          );
        }}
      />
    </Box>
  );
};

export default GrowthFarmAutocomplete;
